// © Microsoft Corporation. All rights reserved.

import { ServiceWorkerCacheName, serviceWorkerCacheHelper } from "@msnews/service-worker-window";
import { appEnvironment, sendToWebWorker } from "@msnews/core";

import { WebWorkerMessageId } from "@msnews/web-worker";
import { getCookie } from "@msnews/cookie-utils";
import { setCookie } from "@msnews/experiences-base";

// Store the 'cbypass' cookie for 10 mins (the HTTP cache header is set for only 5 mins, but we are storing the cookie for a longer duration to avoid any browser quirks)
const cookieExpiryTimeDays = 10 / (24 * 60);

// Common query string name to use when needing to bypass the http cache
export const cacheBypassQueryStringParamName = "cbypass";

let cacheBypassValue: number = parseInt(getCookie(cacheBypassQueryStringParamName)) || 0;

/**
 * Helper that fires the OneServiceContentActions.updateCacheBypassValue action
 */
export async function updateCacheBypassValue(): Promise<void> {
    cacheBypassValue = Date.now();
    setCookie(cacheBypassQueryStringParamName, cacheBypassValue.toString(), cookieExpiryTimeDays, appEnvironment.HostPage.topDomain, "/");

    // purge SW River Cache, need to wait for the purge to complete before we tell WW to fetch river content
    // or we risk it using the currently cached content
    await serviceWorkerCacheHelper.purgeCacheAsync(ServiceWorkerCacheName.river);

    // In the case that webworker is setup to preload the my feed
    // we need to tell it to update every time something triggers the update action.
    // This is so that webworker pulls in the updated feed so it is ready by when the user switches over
    sendToWebWorker({ id: WebWorkerMessageId.UpdateMyFeed, payload: cacheBypassValue });
}

/**
 * Helper that fires the OneServiceContentActions.updateCacheBypassValue action
 */
export async function updateCacheBypassValueWithoutWorker(): Promise<void> {
    cacheBypassValue = Date.now();
    setCookie(cacheBypassQueryStringParamName, cacheBypassValue.toString(), cookieExpiryTimeDays, appEnvironment.HostPage.topDomain, "/");

    // purge SW River Cache, need to wait for the purge to complete before we refresh page or update content
    // or we risk it using the currently cached content
    await serviceWorkerCacheHelper.purgeCacheAsync(ServiceWorkerCacheName.river);
}

/**
 * Helper that gets the current bypass cache value
 * @returns value of cache bypass to use
 */
export function getCurrentCacheBypassValue(): number {
    return cacheBypassValue;
}

/**
 * Helper that takes an URL and adds cache bypass value if one is found
 */
export function includeCacheBypassIfNeeded(url: URL): void {
    if (cacheBypassValue > 0) {
        url.searchParams.set(cacheBypassQueryStringParamName, cacheBypassValue.toString());
    }
}